import * as THREE from 'three';
import {
  IDolumGirdi,
  IDolumMotoru,
  IDolumSonuc,
  IVoxelKafes,
  voxelIndeks,
  voxelMerkez,
} from './IDokumSim';

/**
 * Faz 1 — Dolum (filling) motoru — GERÇEK AKIŞKAN FİZİĞİ (grid CA).
 *
 * Yöntem: voxel kafesi üzerinde KÜTLE + YERÇEKİMİ + BASINÇ hücresel otomatı
 * ("finite water with pressure" / communicating-vessels). Ticari grid çözücü
 * (FLOW-3D/MAGMASOFT) ailesinin hafif, deterministik bir akrabasıdır.
 *
 * Davranış (z-level DEĞİL, gerçek akış):
 *   1. Metal yalnız GİRİŞ voxel'lerinden enjekte edilir (debi ∝ g/s).
 *   2. Her adım kütle önce AŞAĞI (yerçekimi) akar → metal düşer, dibe havuzlanır.
 *   3. Dibi dolunca yanlara yayılır; basınç fazlası YUKARI iter → bağlı kaplar
 *      gibi seviye eşitlenir (girişin altından yükselir, uzağa AKAR → geç dolar).
 *   4. İnce kesit akışa direnir (kalınlık<hücre → akış yavaşlar) → misrun.
 *
 * Çıktı: her voxel için varış zamanı (fillTime). Aktif-hücre takibi ile sadece
 * ıslanan cephe işlenir → büyük kafeste bile hızlı. Saf/deterministik (random yok).
 */

// Akışkan CA sabitleri (MaxMass=1 normalize)
const MAX_KUTLE = 1.0;
const MAX_SIKISMA = 0.02;     // basınç: alttaki hücre biraz fazla taşır → yükselme
const MIN_KUTLE = 5e-4;
const MIN_AKIS = 1e-4;
const DOLU_ESIK = 0.5;        // bu kütlenin üstü "dolu" sayılır (varış anı)
const HEDEF_DOLUM_ITER = 800; // enjeksiyon süresi (adım) — giriş yığılmasını dengeler
const MAX_ITER = 4000;

export class FikoDolumSimulasyon implements IDolumMotoru {

  async hesapla(girdi: IDolumGirdi): Promise<IDolumSonuc> {
    const { kafes, girisler, yukari, akiskanlik, toplamDebiGramSn, yogunlukGcm3 } = girdi;
    const { filtreler, macalar, havalandirmalar } = girdi;
    const [nx, ny, nz] = kafes.boyut;
    const nxy = nx * ny;
    const toplam = nx * ny * nz;
    const kalinlik = kafes.kalinlik;
    const hucreMm = kafes.hucreMm;
    // Maça (core) varsa doluluğu kopyalayıp o bölgeleri dolmaz yap (iç boşluk/engel)
    const doluluk = (macalar && macalar.length) ? this.macalariCikar(kafes, macalar) : kafes.doluluk;
    // Filtre direnç alanı (yakın voxellerde akış kısılır) — yoksa null (=1)
    const filtreDirenc = (filtreler && filtreler.length) ? this.filtreAlani(kafes, filtreler) : null;

    // Yukarı yön (normalize)
    const uy = yukari.clone();
    if (uy.lengthSq() < 1e-12) uy.set(0, 1, 0);
    uy.normalize();

    // ── Yerçekimi ekseni → aşağı/yukarı/yan adımları ───────────────────
    const eksenAdim = [1, nx, nxy];
    const ax = Math.abs(uy.x), ay = Math.abs(uy.y), az = Math.abs(uy.z);
    let gEksen: number, gBilesen: number;
    if (ay >= ax && ay >= az) { gEksen = 1; gBilesen = uy.y; }
    else if (ax >= az) { gEksen = 0; gBilesen = uy.x; }
    else { gEksen = 2; gBilesen = uy.z; }
    const yukariAdim = gBilesen >= 0 ? eksenAdim[gEksen] : -eksenAdim[gEksen];
    const asagiAdim = -yukariAdim;
    const asagiIsaret = asagiAdim > 0 ? 1 : -1;
    const yukariIsaret = yukariAdim > 0 ? 1 : -1;
    // Yan eksenler (yerçekimi ekseni dışındaki ikisi, ±)
    const yanEksenler = [0, 1, 2].filter(e => e !== gEksen);
    const yanAdimlar: number[] = [];
    const yanEksen: number[] = [];
    const yanIsaret: number[] = [];
    for (const e of yanEksenler) {
      yanAdimlar.push(eksenAdim[e], -eksenAdim[e]);
      yanEksen.push(e, e);
      yanIsaret.push(1, -1);
    }

    // Sınır kontrolü (sarma önleme): hücre (ix,iy,iz) o yönde grid içinde mi?
    const gecerli = (ix: number, iy: number, iz: number, eksen: number, isaret: number): boolean => {
      if (eksen === 0) return isaret > 0 ? ix + 1 < nx : ix > 0;
      if (eksen === 1) return isaret > 0 ? iy + 1 < ny : iy > 0;
      return isaret > 0 ? iz + 1 < nz : iz > 0;
    };

    // ── Durum tamponları ───────────────────────────────────────────────
    const m = new Float32Array(toplam);        // sıvı kütlesi [0..~1]
    const delta = new Float32Array(toplam);    // Jacobi akış birikimi
    const dokunulduMu = new Uint8Array(toplam);
    const dokunulan = new Int32Array(kafes.doluSayi + 8);
    let dokN = 0;
    const fillTime = new Float32Array(toplam);
    fillTime.fill(Infinity);

    // Aktif hücre listeleri (çift tampon) — sadece ıslak cephe işlenir
    const cap = kafes.doluSayi + 8;
    let aktif = new Int32Array(cap), aktifN = 0;
    let sonraki = new Int32Array(cap), sonN = 0;
    const aktifMi = new Uint8Array(toplam);     // bir sonraki listede mi (dedupe)

    const sonrakiEkle = (lin: number) => {
      if (lin >= 0 && lin < toplam && doluluk[lin] === 1 && !aktifMi[lin]) {
        aktifMi[lin] = 1; sonraki[sonN++] = lin;
      }
    };
    const deltaEkle = (lin: number, deg: number) => {
      if (!dokunulduMu[lin]) { dokunulduMu[lin] = 1; dokunulan[dokN++] = lin; }
      delta[lin] += deg;
    };

    // ── Giriş tohumları (debi ağırlıklı + zamanlı senaryo) ─────────────
    const tmp = new THREE.Vector3();
    const tohumLin: number[] = [];
    const tohumAgirlik: number[] = [];
    const tohumProfil: ('sabit' | 'rampa' | 'sinus' | 'puls')[] = [];
    const tohumAcilma: number[] = [];   // s — giriş bu anda açılır
    const tohumKapanma: number[] = [];  // s — Infinity = hiç kapanmaz
    let debiTop = 0;
    for (const g of girisler) {
      let lin = voxelIndeks(kafes, g.pozisyon);
      if (lin < 0 || doluluk[lin] !== 1) lin = this.enYakinDolu(kafes, g.pozisyon, tmp);
      if (lin >= 0) {
        tohumLin.push(lin);
        const w = g.debiGramSn > 0 ? g.debiGramSn : 1;
        tohumAgirlik.push(w);
        tohumProfil.push(g.profil ?? 'sabit');
        tohumAcilma.push(Math.max(0, g.acilmaSn ?? 0));
        tohumKapanma.push(g.kapanmaSn && g.kapanmaSn > 0 ? g.kapanmaSn : Infinity);
        debiTop += w;
      }
    }
    if (tohumLin.length === 0) {
      // Giriş yok → boş sonuç (orkestratör zaten varsayılan giriş ekler)
      return { dolumZamani: fillTime, toplamSure: 0, havaCepleri: [], sogukBolgeler: [], dolmayanSayi: kafes.doluSayi };
    }
    if (debiTop <= 0) debiTop = 1;

    // ── Enjeksiyon bütçesi (kütle birimi: dolu voxel = 1) ──────────────
    // Maça varsa fiili dolacak hücre sayısı azalır
    let kapasite = kafes.doluSayi;
    if (macalar && macalar.length) {
      kapasite = 0;
      for (let i = 0; i < toplam; i++) if (doluluk[i] === 1) kapasite++;
    }
    const enjeksiyonPerIter = kapasite / HEDEF_DOLUM_ITER;
    let enjekteToplam = 0;
    // İterasyon → fiziksel zaman eşlemesi (zamanlı giriş senaryosu için):
    // tüm kütle / toplam debi = nominal dolum süresi; HEDEF_DOLUM_ITER adımda enjekte edilir.
    const kutleGTahmin = kafes.hacimCm3 * (yogunlukGcm3 > 0 ? yogunlukGcm3 : 2.5);
    const nominalSure = kutleGTahmin / Math.max(toplamDebiGramSn, 1);
    const iterSn = nominalSure / HEDEF_DOLUM_ITER;
    let sonEnjekteIter = 0; // fiili dökme bitişi (zamanlı girişte uzayabilir)

    // Akışkanlık → yanal yayılma gevşemesi (düşük akışkanlık = yavaş yayılır)
    const akiskanFak = Math.min(1, Math.max(0.25, (akiskanlik || 80) / 100));
    const yanGevseme = 0.5 * akiskanFak;
    // İnce kesit direnci referansı (kalınlık < ~2 hücre → akış kısılır)
    const refKalinlik = hucreMm * 2;

    // ── TERMAL AKIŞ (non-izotermal, Flemings) — opsiyonel ───────────────
    // Metal döküm sıcaklığında girer, akarken ısı taşır (adveksiyon) + kalıba
    // ısı kaybeder (soğuma). T_solidus altı = donmuş (akışı keser → misrun),
    // varış sıcaklığı T_liquidus altı = soğuk birleşme (cold shut).
    const termal = girdi.termal;
    const termalAcik = !!termal;
    const tDokum = termal ? termal.dokumSicaklik : 0;
    const tLiq = termal ? termal.T_liquidus : 0;
    const tSol = termal ? termal.T_solidus : 0;
    const tMold = termal ? termal.kalipSicaklik : 25;
    const T = termalAcik ? new Float32Array(toplam) : null;
    const heatDelta = termalAcik ? new Float32Array(toplam) : null;
    const varisSicaklik = termalAcik ? new Float32Array(toplam) : null;
    const coolFak = termalAcik ? new Float32Array(toplam) : null;
    if (termalAcik && T && coolFak && varisSicaklik) {
      T.fill(tMold);
      varisSicaklik.fill(NaN);
      // Soğuma faktörü: kalıp teması (açıklık) + ince kesit (yüzey/hacim). Kalibre.
      const sogCarpan = (termal!.soguma ?? 1);
      const taban = 2.5e-5 * sogCarpan; // yumuşak — dolum tamamlansın, aşırı cold-shut olmasın
      for (let i = 0; i < toplam; i++) {
        if (doluluk[i] !== 1) continue;
        const ix = i % nx, iy = ((i / nx) | 0) % ny, iz = (i / nxy) | 0;
        let acik = 0;
        if (ix + 1 >= nx || doluluk[i + 1] !== 1) acik++;
        if (ix - 1 < 0 || doluluk[i - 1] !== 1) acik++;
        if (iy + 1 >= ny || doluluk[i + nx] !== 1) acik++;
        if (iy - 1 < 0 || doluluk[i - nx] !== 1) acik++;
        if (iz + 1 >= nz || doluluk[i + nxy] !== 1) acik++;
        if (iz - 1 < 0 || doluluk[i - nxy] !== 1) acik++;
        const acikOran = acik / 6;
        const kal = kalinlik[i];
        const inceMult = kal > 0 ? Math.min(4, Math.max(1, (refKalinlik * 2) / kal)) : 2;
        coolFak[i] = Math.min(0.06, taban * (0.3 + acikOran) * inceMult);
      }
    }

    // Başlangıç aktifleri = giriş tohumları
    for (const s of tohumLin) sonrakiEkle(s);
    // sonraki → aktif ilk yükleme
    [aktif, sonraki] = [sonraki, aktif]; aktifN = sonN; sonN = 0;
    for (let p = 0; p < aktifN; p++) aktifMi[aktif[p]] = 0;

    // Dengeli durum: alt hücrede kalması gereken kütle (basınç modeli)
    const dengeKutle = (toplamK: number): number => {
      if (toplamK <= MAX_KUTLE) return toplamK;
      if (toplamK < 2 * MAX_KUTLE + MAX_SIKISMA)
        return (MAX_KUTLE * MAX_KUTLE + toplamK * MAX_SIKISMA) / (MAX_KUTLE + MAX_SIKISMA);
      return (toplamK + MAX_SIKISMA) / 2;
    };

    let doluSay = 0;
    let iter = 0;
    for (; iter < MAX_ITER; iter++) {
      // 1) Enjeksiyon (bütçe bitene kadar) — akış profili + zamanlı aç/kapa senaryosu
      if (enjekteToplam < kapasite) {
        const tSim = iter * iterSn; // fiziksel zaman (s)
        for (let t = 0; t < tohumLin.length; t++) {
          const kalan = kapasite - enjekteToplam;
          if (kalan <= 0) break;
          // Zamanlı senaryo: giriş henüz açılmadı veya kapandı → bu iterde akmaz
          if (tSim < tohumAcilma[t] || tSim >= tohumKapanma[t]) continue;
          // Profil fazı girişin KENDİ aktif penceresine göre (açılınca 0'dan başlar)
          const fazG = (tSim - tohumAcilma[t]) / Math.max(nominalSure - tohumAcilma[t], iterSn);
          const fak = this.profilFak(tohumProfil[t], Math.min(1, fazG));
          const pay = enjeksiyonPerIter * (tohumAgirlik[t] / debiTop) * fak;
          const ekle = Math.min(pay, kalan);
          if (ekle <= 1e-9) continue;
          const s = tohumLin[t];
          if (termalAcik && T) { const mo = m[s]; T[s] = (mo * T[s] + ekle * tDokum) / (mo + ekle); }
          m[s] += ekle;
          enjekteToplam += ekle;
          sonEnjekteIter = iter;
          sonrakiEkle(s);
        }
      }

      // 2) Akış (aktif hücreler üzerinde Jacobi: m oku, delta yaz)
      for (let p = 0; p < aktifN; p++) {
        const i = aktif[p];
        let rem = m[i];
        if (rem < MIN_KUTLE) continue;
        const ix = i % nx, iy = ((i / nx) | 0) % ny, iz = (i / nxy) | 0;

        // İnce kesit direnci + filtre direnci: akış kısılır
        const kal = kalinlik[i];
        let kesitFak = kal > 0 && kal < refKalinlik ? Math.max(0.3, kal / refKalinlik) : 1;
        if (filtreDirenc) kesitFak *= filtreDirenc[i];

        // Soğuyan metal akışa direnir (donma noktasına yakın → yavaşlar) ama
        // tamamen DURMAZ (yoksa ince kesit dolmadan misrun olur). Akış faktörü.
        const Ti = termalAcik && T ? T[i] : 0; // adveksiyon için taşınan sıcaklık
        if (termalAcik && T) {
          const yarisivi = (Ti - tSol) / Math.max(tLiq - tSol, 1e-3); // 1 sıvı, 0 katı
          kesitFak *= Math.max(0.5, Math.min(1, 0.5 + 0.5 * yarisivi));
        }

        // a) AŞAĞI — yerçekimi
        if (gecerli(ix, iy, iz, gEksen, asagiIsaret)) {
          const d = i + asagiAdim;
          if (doluluk[d] === 1) {
            let akis = dengeKutle(rem + m[d]) - m[d];
            akis = Math.min(akis, rem) * kesitFak;
            if (akis > MIN_AKIS) {
              deltaEkle(d, akis); deltaEkle(i, -akis); rem -= akis;
              if (heatDelta) { heatDelta[d] += akis * Ti; heatDelta[i] -= akis * Ti; }
              sonrakiEkle(d);
            }
          }
        }

        // b) YANLAR — seviye eşitleme
        if (rem > MIN_KUTLE) {
          for (let s = 0; s < yanAdimlar.length; s++) {
            if (!gecerli(ix, iy, iz, yanEksen[s], yanIsaret[s])) continue;
            const v = i + yanAdimlar[s];
            if (doluluk[v] !== 1) continue;
            const fark = rem - m[v];
            if (fark > 0) {
              let akis = (fark * 0.25) * yanGevseme * kesitFak;
              akis = Math.min(akis, rem);
              if (akis > MIN_AKIS) {
                deltaEkle(v, akis); deltaEkle(i, -akis); rem -= akis;
                if (heatDelta) { heatDelta[v] += akis * Ti; heatDelta[i] -= akis * Ti; }
                sonrakiEkle(v);
              }
            }
          }
        }

        // c) YUKARI — basınç fazlası (communicating vessels)
        if (rem > MAX_KUTLE && gecerli(ix, iy, iz, gEksen, yukariIsaret)) {
          const u = i + yukariAdim;
          if (doluluk[u] === 1) {
            let akis = rem - dengeKutle(rem + m[u]);
            akis = Math.min(akis, rem) * kesitFak;
            if (akis > MIN_AKIS) {
              deltaEkle(u, akis); deltaEkle(i, -akis); rem -= akis;
              if (heatDelta) { heatDelta[u] += akis * Ti; heatDelta[i] -= akis * Ti; }
              sonrakiEkle(u);
            }
          }
        }
        // Değişen kaynak/alıcı hücreler delta üzerinden (adım 3) yeniden
        // aktive olur; akışsız (yerleşmiş) hücre listeden düşer → cephe ince kalır.
      }

      // 3) Delta uygula + fillTime kaydet + komşuları aktive et
      for (let d = 0; d < dokN; d++) {
        const idx = dokunulan[d];
        // Termal: önce sıcaklığı entalpi (m·T) bilançosuyla güncelle, sonra m
        if (termalAcik && T && heatDelta) {
          const mAfter = m[idx] + delta[idx];
          if (mAfter > MIN_KUTLE) {
            T[idx] = (m[idx] * T[idx] + heatDelta[idx]) / mAfter;
            T[idx] -= (T[idx] - tMold) * coolFak![idx]; // kalıba ısı kaybı
          }
          heatDelta[idx] = 0;
        }
        m[idx] += delta[idx];
        if (m[idx] < 0) m[idx] = 0;
        delta[idx] = 0;
        dokunulduMu[idx] = 0;
        if (fillTime[idx] === Infinity && m[idx] >= DOLU_ESIK) {
          fillTime[idx] = iter;
          doluSay++;
          if (termalAcik && T && varisSicaklik) varisSicaklik[idx] = T[idx]; // varış sıcaklığı
        }
        // Değişen hücre + komşuları cephede kalsın: bir hücre boşalınca
        // üstündeki/yanındaki metal o boşluğa akabilmeli (cephe ilerler).
        sonrakiEkle(idx);
        const ix = idx % nx, iy = ((idx / nx) | 0) % ny, iz = (idx / nxy) | 0;
        if (ix + 1 < nx) sonrakiEkle(idx + 1);
        if (ix > 0) sonrakiEkle(idx - 1);
        if (iy + 1 < ny) sonrakiEkle(idx + nx);
        if (iy > 0) sonrakiEkle(idx - nx);
        if (iz + 1 < nz) sonrakiEkle(idx + nxy);
        if (iz > 0) sonrakiEkle(idx - nxy);
      }
      dokN = 0;

      // 4) Çift tampon takası
      for (let p = 0; p < sonN; p++) aktifMi[sonraki[p]] = 0;
      [aktif, sonraki] = [sonraki, aktif];
      aktifN = sonN; sonN = 0;

      // 5) Sonlanma
      if (enjekteToplam >= kapasite - 1e-6 && aktifN === 0) break;
      if (doluSay >= kapasite * 0.999) break;
      // Zamanlı senaryo: tüm girişler kapandı + ileride açılacak yok + akış durdu → bitir
      if (aktifN === 0 && enjekteToplam < kapasite) {
        const tSim = iter * iterSn;
        let gelecekVar = false;
        for (let t = 0; t < tohumAcilma.length; t++) {
          if (tSim < tohumAcilma[t]) { gelecekVar = true; break; }
        }
        if (!gelecekVar) break;
      }

      // UI'ya nefes ver + ilerleme (ana thread donmasın)
      if ((iter & 31) === 0) {
        if (girdi.ilerle) girdi.ilerle(Math.min(98, Math.round((doluSay / Math.max(kapasite, 1)) * 100)));
        await new Promise(r => setTimeout(r, 0));
      }
    }

    // ── Bağlantı tamamlama (komple dolum) ──────────────────────────────
    // Basınç/seviye modeli ince yüksek bölgelere ulaşamadıysa: girişe metalle
    // BAĞLI tüm boş voxelleri BFS ile doldur (gerçekte bağlı kavite komple dolar).
    // Sadece tamamen KOPUK bölge boş kalır = gerçek misrun.
    {
      const kuyruk = new Int32Array(toplam);
      let kb = 0, ks = 0, maxIt = 0;
      for (let i = 0; i < toplam; i++) {
        if (fillTime[i] !== Infinity) { if (fillTime[i] > maxIt) maxIt = fillTime[i]; kuyruk[kb++] = i; }
      }
      const komsu = [1, -1, nx, -nx, nxy, -nxy];
      while (ks < kb) {
        const u = kuyruk[ks++];
        const ux = u % nx, uy = ((u / nx) | 0) % ny, uz = (u / nxy) | 0;
        for (let c = 0; c < 6; c++) {
          const v = u + komsu[c];
          if (v < 0 || v >= toplam || doluluk[v] !== 1 || fillTime[v] !== Infinity) continue;
          if (komsu[c] === 1 && ux + 1 >= nx) continue;
          if (komsu[c] === -1 && ux - 1 < 0) continue;
          if (komsu[c] === nx && uy + 1 >= ny) continue;
          if (komsu[c] === -nx && uy - 1 < 0) continue;
          if (komsu[c] === nxy && uz + 1 >= nz) continue;
          if (komsu[c] === -nxy && uz - 1 < 0) continue;
          fillTime[v] = fillTime[u] + 1; // cephe sırasını devam ettir
          if (termalAcik && T && varisSicaklik) {
            const tv = T[u]; T[v] = tv;
            varisSicaklik[v] = isFinite(varisSicaklik[u]) ? varisSicaklik[u] : tv;
          }
          kuyruk[kb++] = v;
        }
      }
    }

    // ── Süre ölçeği: varış adımı → fiziksel zaman (kütle/debi) ──────────
    // Zamanlı girişlerde fiili dökme süresi uzar (geç açılan giriş) → onu esas al.
    const kutleG = kafes.hacimCm3 * yogunlukGcm3;
    const debi = toplamDebiGramSn > 0 ? toplamDebiGramSn : 1;
    const toplamSure = Math.max(kutleG / debi, sonEnjekteIter * iterSn);
    let maxIterUlasilan = 1;
    for (let i = 0; i < toplam; i++) {
      const f = fillTime[i];
      if (f !== Infinity && f > maxIterUlasilan) maxIterUlasilan = f;
    }
    const dolumZamani = fillTime; // yeniden kullan
    for (let i = 0; i < toplam; i++) {
      const f = fillTime[i];
      dolumZamani[i] = f === Infinity ? Infinity : (f / maxIterUlasilan) * toplamSure;
    }

    // ── Misrun: girişten metalle dolmayan kavite voxel'leri ────────────
    let dolmayanSayi = 0;
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] === 1 && dolumZamani[i] === Infinity) dolmayanSayi++;
    }

    // Hapsolan hava — hava deliği yakınındaki cepler tahliye edilir (kusurdan düşer)
    let havaCepleri = this.hapsolanHava(kafes, uy);
    if (havalandirmalar && havalandirmalar.length) {
      havaCepleri = havaCepleri.filter(c => !this.noktaYakin(havalandirmalar, c.merkez));
    }
    const sogukBolgeler = this.sogukBolgeBul(kafes, dolumZamani, toplamSure);

    // ── Termal: varış sıcaklığı temizliği + soğuk birleşme (cold shut) ──
    let coldShutBolgeler: { merkez: THREE.Vector3; siddet: number }[] | undefined;
    if (termalAcik && T && varisSicaklik) {
      // Dolmayan/NaN kavite hücreleri katılaşma için makul başlangıca çek
      for (let i = 0; i < toplam; i++) {
        if (doluluk[i] !== 1) continue;
        const v = varisSicaklik[i];
        if (!isFinite(v)) varisSicaklik[i] = (dolumZamani[i] === Infinity) ? tSol : tDokum;
      }
      // Cold shut: liquidus altında dolan hücreler (zayıf birleşme) → kümele
      const csAlan = new Float32Array(toplam);
      for (let i = 0; i < toplam; i++) {
        if (doluluk[i] !== 1 || dolumZamani[i] === Infinity) continue;
        const v = varisSicaklik[i];
        if (v < tLiq) csAlan[i] = Math.min(1, (tLiq - v) / Math.max(tLiq - tSol, 1e-3));
      }
      coldShutBolgeler = this.alanKumele(kafes, csAlan, 0.05);
    }

    return {
      dolumZamani, toplamSure, havaCepleri, sogukBolgeler, dolmayanSayi,
      varisSicaklik: varisSicaklik ?? null,
      coldShutBolgeler,
    };
  }

  /** Pozitif şiddet alanını 6-bağ bağlı bileşene böl (merkez + ort. şiddet). */
  private alanKumele(
    k: IVoxelKafes, alan: Float32Array, esik: number,
  ): { merkez: THREE.Vector3; siddet: number }[] {
    const [nx, ny, nz] = k.boyut;
    const toplam = nx * ny * nz;
    const nxny = nx * ny;
    const ziyaret = new Uint8Array(toplam);
    const yigin = new Int32Array(toplam);
    const komsu = [1, -1, nx, -nx, nxny, -nxny];
    const merkez = new THREE.Vector3();
    const sonuc: { merkez: THREE.Vector3; siddet: number }[] = [];
    for (let s = 0; s < toplam; s++) {
      if (alan[s] <= esik || ziyaret[s]) continue;
      let yN = 0; yigin[yN++] = s; ziyaret[s] = 1;
      let cx = 0, cy = 0, cz = 0, say = 0, top = 0;
      while (yN > 0) {
        const u = yigin[--yN];
        const ux = u % nx, uyi = ((u / nx) | 0) % ny, uzi = (u / nxny) | 0;
        cx += ux; cy += uyi; cz += uzi; say++; top += alan[u];
        for (let c = 0; c < 6; c++) {
          const v = u + komsu[c];
          if (v < 0 || v >= toplam || ziyaret[v] || alan[v] <= esik) continue;
          if (komsu[c] === 1 && ux + 1 >= nx) continue;
          if (komsu[c] === -1 && ux - 1 < 0) continue;
          if (komsu[c] === nx && uyi + 1 >= ny) continue;
          if (komsu[c] === -nx && uyi - 1 < 0) continue;
          ziyaret[v] = 1; yigin[yN++] = v;
        }
      }
      if (say >= 3) {
        const lin = Math.min(toplam - 1, Math.max(0,
          Math.round(cx / say) + nx * (Math.round(cy / say) + ny * Math.round(cz / say))));
        voxelMerkez(k, lin, merkez);
        sonuc.push({ merkez: merkez.clone(), siddet: Math.min(1, top / say) });
      }
    }
    return sonuc;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Akış profili faktörü: faz∈[0,1] enjeksiyon ilerlemesi → debi çarpanı.
  // ─────────────────────────────────────────────────────────────────────
  private profilFak(profil: 'sabit' | 'rampa' | 'sinus' | 'puls', faz: number): number {
    switch (profil) {
      case 'rampa': return Math.min(1, faz / 0.25);                       // ilk %25'te 0→1
      case 'sinus': return 0.55 + 0.45 * Math.sin(2 * Math.PI * faz * 4); // dalgalı dökme
      case 'puls':  return (Math.floor(faz * 10) % 2 === 0) ? 1 : 0.3;    // aç/kıs
      default:      return 1;
    }
  }

  /** Maça (core) bölgelerini doluluktan çıkar (o voxeller dolmaz). */
  private macalariCikar(k: IVoxelKafes, macalar: { pozisyon: THREE.Vector3; capMm: number }[]): Uint8Array {
    const out = k.doluluk.slice();
    this.bolgeleriTara(k, macalar, (lin) => { out[lin] = 0; });
    return out;
  }

  /** Filtre yakını voxellerde akış direnci alanı (1 = serbest, <1 = kısık). */
  private filtreAlani(k: IVoxelKafes, filtreler: { pozisyon: THREE.Vector3; capMm: number }[]): Float32Array {
    const [nx, ny, nz] = k.boyut;
    const alan = new Float32Array(nx * ny * nz).fill(1);
    this.bolgeleriTara(k, filtreler, (lin) => { alan[lin] = 0.4; }); // %60 direnç
    return alan;
  }

  /** Bir noktanın herhangi bir bölge (konum+yarıçap) içinde/yakınında olması. */
  private noktaYakin(bolgeler: { pozisyon: THREE.Vector3; capMm: number }[], p: THREE.Vector3): boolean {
    for (const b of bolgeler) {
      const r = b.capMm * 0.5 + 8; // yarıçap + küçük pay (mm)
      if (b.pozisyon.distanceToSquared(p) <= r * r) return true;
    }
    return false;
  }

  /** Bölge listesindeki her küre içindeki voxellere callback uygula. */
  private bolgeleriTara(
    k: IVoxelKafes,
    bolgeler: { pozisyon: THREE.Vector3; capMm: number }[],
    cb: (lin: number) => void,
  ): void {
    const [nx, ny, nz] = k.boyut;
    const h = k.hucreMm;
    for (const b of bolgeler) {
      const r = Math.max(b.capMm * 0.5, h);
      const cx = (b.pozisyon.x - k.origin.x) / h;
      const cy = (b.pozisyon.y - k.origin.y) / h;
      const cz = (b.pozisyon.z - k.origin.z) / h;
      const rv = r / h;
      const x0 = Math.max(0, Math.floor(cx - rv)), x1 = Math.min(nx - 1, Math.ceil(cx + rv));
      const y0 = Math.max(0, Math.floor(cy - rv)), y1 = Math.min(ny - 1, Math.ceil(cy + rv));
      const z0 = Math.max(0, Math.floor(cz - rv)), z1 = Math.min(nz - 1, Math.ceil(cz + rv));
      const rv2 = rv * rv;
      for (let z = z0; z <= z1; z++) {
        const dz = z + 0.5 - cz;
        for (let y = y0; y <= y1; y++) {
          const dy = y + 0.5 - cy;
          for (let x = x0; x <= x1; x++) {
            const dx = x + 0.5 - cx;
            if (dx * dx + dy * dy + dz * dz <= rv2) cb(x + nx * (y + ny * z));
          }
        }
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────
  // Giriş boştaysa en yakın dolu voxel'i bul (genişleyen küp tarama)
  // ─────────────────────────────────────────────────────────────────────
  private enYakinDolu(k: IVoxelKafes, p: THREE.Vector3, _tmp: THREE.Vector3): number {
    const [nx, ny, nz] = k.boyut;
    const doluluk = k.doluluk;
    let ix = Math.floor((p.x - k.origin.x) / k.hucreMm);
    let iy = Math.floor((p.y - k.origin.y) / k.hucreMm);
    let iz = Math.floor((p.z - k.origin.z) / k.hucreMm);
    ix = Math.min(nx - 1, Math.max(0, ix));
    iy = Math.min(ny - 1, Math.max(0, iy));
    iz = Math.min(nz - 1, Math.max(0, iz));
    const maxR = Math.max(nx, ny, nz);
    for (let r = 0; r <= maxR; r++) {
      const x0 = Math.max(0, ix - r), x1 = Math.min(nx - 1, ix + r);
      const y0 = Math.max(0, iy - r), y1 = Math.min(ny - 1, iy + r);
      const z0 = Math.max(0, iz - r), z1 = Math.min(nz - 1, iz + r);
      for (let z = z0; z <= z1; z++) {
        for (let y = y0; y <= y1; y++) {
          for (let x = x0; x <= x1; x++) {
            if (r > 0 && x > x0 && x < x1 && y > y0 && y < y1 && z > z0 && z < z1) continue;
            const lin = x + nx * (y + ny * z);
            if (doluluk[lin] === 1) return lin;
          }
        }
      }
    }
    return -1;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Hapsolan hava: yukari yönünde üstü kapalı yerel tepe cepleri.
  // ─────────────────────────────────────────────────────────────────────
  private hapsolanHava(k: IVoxelKafes, uy: THREE.Vector3): { merkez: THREE.Vector3; hucreSayisi: number }[] {
    const [nx, ny, nz] = k.boyut;
    const toplam = nx * ny * nz;
    const doluluk = k.doluluk;

    const ax = Math.abs(uy.x), ay = Math.abs(uy.y), az = Math.abs(uy.z);
    let upStep: number;
    if (ay >= ax && ay >= az) upStep = uy.y >= 0 ? nx : -nx;
    else if (ax >= az) upStep = uy.x >= 0 ? 1 : -1;
    else upStep = uy.z >= 0 ? nx * ny : -(nx * ny);

    const tepe = new Uint8Array(toplam);
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] !== 1) continue;
      const ust = i + upStep;
      const ustBos = (ust < 0 || ust >= toplam || doluluk[ust] !== 1);
      if (ustBos) tepe[i] = 1;
    }

    const ziyaret = new Uint8Array(toplam);
    const yigin = new Int32Array(toplam);
    const cepler: { merkez: THREE.Vector3; hucreSayisi: number }[] = [];
    const merkez = new THREE.Vector3();
    const komsu = [1, -1, nx, -nx, nx * ny, -(nx * ny)];

    for (let s = 0; s < toplam; s++) {
      if (tepe[s] !== 1 || ziyaret[s]) continue;
      let yN = 0;
      yigin[yN++] = s; ziyaret[s] = 1;
      let cx = 0, cy = 0, cz = 0, say = 0;
      while (yN > 0) {
        const u = yigin[--yN];
        const ux = u % nx;
        const uyi = ((u / nx) | 0) % ny;
        const uzi = (u / (nx * ny)) | 0;
        cx += ux; cy += uyi; cz += uzi; say++;
        for (let c = 0; c < 6; c++) {
          const v = u + komsu[c];
          if (v < 0 || v >= toplam || ziyaret[v] || tepe[v] !== 1) continue;
          if (komsu[c] === 1 && ux + 1 >= nx) continue;
          if (komsu[c] === -1 && ux - 1 < 0) continue;
          if (komsu[c] === nx && uyi + 1 >= ny) continue;
          if (komsu[c] === -nx && uyi - 1 < 0) continue;
          ziyaret[v] = 1;
          yigin[yN++] = v;
        }
      }
      if (say >= 3) {
        const linMerkez = Math.round(cx / say) + nx * (Math.round(cy / say) + ny * Math.round(cz / say));
        voxelMerkez(k, Math.min(toplam - 1, Math.max(0, linMerkez)), merkez);
        cepler.push({ merkez: merkez.clone(), hucreSayisi: say });
      }
    }
    return cepler;
  }

  // ─────────────────────────────────────────────────────────────────────
  // Soğuk birleşme/eksik dolum: geç dolan + ince kesit voxel'leri.
  // ─────────────────────────────────────────────────────────────────────
  private sogukBolgeBul(
    k: IVoxelKafes,
    dolumZamani: Float32Array,
    toplamSure: number,
  ): { merkez: THREE.Vector3; siddet: number }[] {
    const [nx, ny, nz] = k.boyut;
    const toplam = nx * ny * nz;
    const doluluk = k.doluluk;
    const kalinlik = k.kalinlik;
    const refKalinlik = k.hucreMm * 4;

    const sureEsik = toplamSure * 0.7;
    const risk = new Float32Array(toplam);
    for (let i = 0; i < toplam; i++) {
      if (doluluk[i] !== 1) continue;
      const f = dolumZamani[i];
      let zamanRisk: number;
      if (f === Infinity) zamanRisk = 1;
      else if (f >= sureEsik) zamanRisk = (f - sureEsik) / Math.max(1e-6, toplamSure - sureEsik);
      else continue;
      const kal = kalinlik[i] > 0 ? kalinlik[i] : refKalinlik;
      const inceRisk = Math.min(1, refKalinlik / kal - 1);
      const siddet = Math.min(1, 0.5 * zamanRisk + 0.5 * Math.max(0, inceRisk) + 0.15 * zamanRisk);
      if (siddet > 0.25) risk[i] = Math.min(1, siddet);
    }

    const ziyaret = new Uint8Array(toplam);
    const yigin = new Int32Array(toplam);
    const komsu = [1, -1, nx, -nx, nx * ny, -(nx * ny)];
    const merkez = new THREE.Vector3();
    const bolgeler: { merkez: THREE.Vector3; siddet: number }[] = [];

    for (let s = 0; s < toplam; s++) {
      if (risk[s] <= 0 || ziyaret[s]) continue;
      let yN = 0;
      yigin[yN++] = s; ziyaret[s] = 1;
      let cx = 0, cy = 0, cz = 0, say = 0, siddetTop = 0;
      while (yN > 0) {
        const u = yigin[--yN];
        const ux = u % nx;
        const uyi = ((u / nx) | 0) % ny;
        const uzi = (u / (nx * ny)) | 0;
        cx += ux; cy += uyi; cz += uzi; say++;
        siddetTop += risk[u];
        for (let c = 0; c < 6; c++) {
          const v = u + komsu[c];
          if (v < 0 || v >= toplam || ziyaret[v] || risk[v] <= 0) continue;
          if (komsu[c] === 1 && ux + 1 >= nx) continue;
          if (komsu[c] === -1 && ux - 1 < 0) continue;
          if (komsu[c] === nx && uyi + 1 >= ny) continue;
          if (komsu[c] === -nx && uyi - 1 < 0) continue;
          ziyaret[v] = 1;
          yigin[yN++] = v;
        }
      }
      if (say >= 3) {
        const linMerkez = Math.round(cx / say) + nx * (Math.round(cy / say) + ny * Math.round(cz / say));
        voxelMerkez(k, Math.min(toplam - 1, Math.max(0, linMerkez)), merkez);
        bolgeler.push({ merkez: merkez.clone(), siddet: Math.min(1, siddetTop / say) });
      }
    }
    return bolgeler;
  }
}
